import { cn } from "../../../../utils/classnames";
import {
  EyeButton,
  SortIcon,
  TrendIcon,
} from "./Icons";

const STATUS_STYLES = {
  pending: "tw:bg-[#F6BB2A]",
  approved: "tw:bg-[#3CAEA3]",
  rejected: "tw:bg-[#EA4335]",
  updated: "tw:bg-[#F9EDD3] tw:!text-[#A3780F]",
  draft: "tw:bg-[#E8E8E8] tw:!text-[#7C7B7B]",
};

const REQUEST_TYPE_STYLES = {
  reschedule: "tw:border-[#3CAEA3] tw:text-[#3CAEA3]",
  cancel: "tw:border-[#EA4335] tw:text-[#EA4335]",
};

const TABLE_COLUMNS = [
  { key: "no", title: "#", width: "tw:w-[50px] tw:rounded-tl-[5px]" },
  { key: "holder", title: "Reservation Holder", width: "tw:w-[175px]" },
  { key: "requestedOn", title: "Requested On", width: "tw:w-[150px]", sortable: true, sortField: "date" },
  { key: "requestType", title: "Request Type", width: "tw:w-[150px]" },
  { key: "status", title: "Status", width: "tw:w-[205px]", sortable: true, sortField: "status" },
  { key: "userType", title: "User Type", width: "tw:w-[150px]" },
  { key: "paymentReceived", title: "Payment Received", width: "tw:w-[160px]" },
  { key: "newPrice", title: "New Price", width: "tw:w-[300px]" },
  { key: "adminUpdate", title: "Admin Update", width: "tw:w-[150px]" },
  { key: "admin", title: "Admin", width: "tw:w-[150px]" },
  { key: "action", title: "Action", width: "tw:w-[62px] tw:rounded-tr-[5px]", sticky: true },
];

const TABLE_COLUMN_COUNT = TABLE_COLUMNS.length;

const HeaderCell = ({ title, width, sortable, sortField, orderBy, sortBy, onSort, sticky }) => (
  <th
    className={cn(
      "tw:h-[60px] tw:bg-[#282828] tw:px-[15px] tw:py-[20px] tw:text-left tw:text-[15px] tw:font-bold tw:leading-[22px] tw:text-white",
      width,
      sortable && "tw:cursor-pointer tw:select-none",
      sticky && "tw:sticky tw:right-0 tw:z-20",
    )}
    onClick={sortable ? () => onSort(sortField) : undefined}
  >
    <div className={cn("tw:flex tw:items-center", sortable && "tw:justify-between")}>
      <span className="tw:whitespace-nowrap">{title}</span>
      {sortable ? <SortIcon direction={orderBy === sortField ? sortBy : null} /> : null}
    </div>
  </th>
);

const MessageRow = ({ className, text }) => (
  <tr>
    <td colSpan={TABLE_COLUMN_COUNT} className={className}>
      {text}
    </td>
  </tr>
);

const baseCellClass = "tw:h-[75px] tw:border-b tw:border-[#F3F3F3] tw:bg-white tw:px-[15px] tw:text-[15px] tw:leading-[22px]";

const RequestsTable = ({ rows, isLoadingRows, requestError, orderBy, sortBy, onSort }) => (
  <section className="tw:mt-[34px] tw:w-full tw:overflow-x-auto">
    <table className="tw:w-full tw:min-w-[1315px] tw:border-separate tw:border-spacing-0">
      <thead>
        <tr>
          {TABLE_COLUMNS.map((column) => (
            <HeaderCell
              key={column.key}
              title={column.title}
              width={column.width}
              sortable={column.sortable}
              sortField={column.sortField}
              orderBy={orderBy}
              sortBy={sortBy}
              onSort={onSort}
              sticky={column.sticky}
            />
          ))}
        </tr>
      </thead>
      <tbody>
        {isLoadingRows ? (
          <MessageRow
            className={`${baseCellClass} tw:text-center tw:text-[#7C7B7B]`}
            text="Loading modification requests..."
          />
        ) : null}

        {!isLoadingRows && requestError ? (
          <MessageRow
            className={`${baseCellClass} tw:text-center tw:text-[#EA4335]`}
            text={requestError}
          />
        ) : null}

        {!isLoadingRows && !requestError && rows.length === 0 ? (
          <MessageRow
            className={`${baseCellClass} tw:text-center tw:text-[#7C7B7B]`}
            text="No modification requests found."
          />
        ) : null}

        {!isLoadingRows && !requestError
          ? rows.map((row, index) => (
              <tr key={row.id}>
                <td className={baseCellClass}>{index + 1}</td>
                <td className={baseCellClass}>{row.holder}</td>
                <td className={baseCellClass}>
                  <span className="tw:block">{row.requestedOn[0]}</span>
                  <span className="tw:block">{row.requestedOn[1]}</span>
                </td>
                <td className="tw:h-[75px] tw:border-b tw:border-[#F3F3F3] tw:bg-white tw:px-[15px]">
                  <span
                    className={cn(
                      "tw:inline-flex tw:h-[45px] tw:items-center tw:justify-center tw:whitespace-nowrap tw:rounded-[5px] tw:border-2 tw:bg-transparent tw:px-6 tw:text-[15px] tw:font-medium tw:capitalize tw:leading-[22px]",
                      REQUEST_TYPE_STYLES[row.requestType],
                    )}
                  >
                    {row.requestType}
                  </span>
                </td>
                <td className="tw:h-[75px] tw:border-b tw:border-[#F3F3F3] tw:bg-white tw:px-[15px]">
                  <span
                    className={cn(
                      "tw:inline-flex tw:h-[45px] tw:items-center tw:justify-center tw:whitespace-nowrap tw:rounded-[5px] tw:px-6 tw:text-[15px] tw:leading-[22px] tw:text-white",
                      STATUS_STYLES[row.statusType],
                    )}
                  >
                    {row.status}
                  </span>
                </td>
                <td className={baseCellClass}>{row.userType}</td>
                <td className={baseCellClass}>{row.paymentReceived}</td>
                <td className={`${baseCellClass} tw:whitespace-nowrap`}>
                  <div className="tw:flex tw:items-center tw:gap-[6px]">
                    <span>{row.newPrice}</span>
                    <TrendIcon trend={row.trend} />
                  </div>
                </td>
                <td className={baseCellClass}>
                  {Array.isArray(row.adminUpdate) ? (
                    <>
                      <span className="tw:block">{row.adminUpdate[0]}</span>
                      <span className="tw:block">{row.adminUpdate[1]}</span>
                    </>
                  ) : (
                    row.adminUpdate
                  )}
                </td>
                <td className={baseCellClass}>{row.admin}</td>
                <td className="tw:sticky tw:right-0 tw:z-10 tw:h-[75px] tw:border-b tw:border-x tw:border-[#F3F3F3] tw:bg-white tw:px-[15px] tw:text-center">
                  <EyeButton id={row.id} />
                </td>
              </tr>
            ))
          : null}
      </tbody>
    </table>
  </section>
);

export default RequestsTable;
